import { useMutation, useQuery } from "@tanstack/react-query";
import { Organization } from "@common/types/organization";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { organizationContract } from "@common/contracts/organization.contract";
import { hasAccessToken } from "@lib/auth0";
import z from "zod";
import { queryClient } from "queryClient";
import {
    OrganizationIdNameLogoUrl,
    OrganizationIdNameLogoUrlArraySchema,
    OrganizationDtoSchema,
    OrganizationProfile,
} from "@common/schemas/organization.schema";

const organizationContractClient = getBackendAPIClient(organizationContract);

export async function getOrganization(): Promise<Organization | null> {
    const result = await organizationContractClient.getOrganization();

    if (!result.body) {
        return null;
    }

    return OrganizationDtoSchema.parse(result.body);
}

async function getOrganizationsList(): Promise<OrganizationIdNameLogoUrl[]> {
    const result = await organizationContractClient.listOrganizations();

    return OrganizationIdNameLogoUrlArraySchema.parse(
        result.body.organizations,
    );
}

export const organizationQueryKeys = {
    all: () => ["organization"],
    get: () => [...organizationQueryKeys.all(), "get"],
    list: () => [...organizationQueryKeys.all(), "list"],
} as const;

export function useOrganization() {
    return useQuery({
        queryKey: organizationQueryKeys.get(),
        queryFn: async () => await getOrganization(),
        staleTime: 10 * 60 * 1000, // 10 minutes
        enabled: hasAccessToken(),
    });
}

export function useOrganizationsList(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: organizationQueryKeys.list(),
        queryFn: async () => await getOrganizationsList(),
        staleTime: 10 * 60 * 1000,
        enabled: options?.enabled,
    });
}

type CreateOrganizationPayload = z.infer<
    typeof organizationContract.createOrganization.body
>;

export function useCreateOrganizationMutation() {
    return useMutation({
        mutationFn: async (payload: CreateOrganizationPayload) => {
            const result = await organizationContractClient.createOrganization({
                body: payload,
            });

            return OrganizationDtoSchema.parse(result.body);
        },
        onSuccess: (organization) => {
            queryClient.setQueryData(
                organizationQueryKeys.get(),
                () => organization,
            );

            void queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.get(),
            });
        },
    });
}

type CreateOrganizationForOnboardingPayload = z.infer<
    typeof organizationContract.createOrganizationForOnboarding.body
>;

export function useCreateOrganizationForOnboardingMutation() {
    return useMutation({
        mutationFn: async (payload: CreateOrganizationForOnboardingPayload) => {
            const result =
                await organizationContractClient.createOrganizationForOnboarding(
                    {
                        body: payload,
                    },
                );

            return OrganizationDtoSchema.parse(result.body);
        },
        onSuccess: (organization) => {
            queryClient.setQueryData(
                organizationQueryKeys.get(),
                () => organization,
            );

            void queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.get(),
            });
        },
    });
}

type UpdateOrganizationPayload = z.infer<
    typeof organizationContract.updateOrganization.body
>;

interface UpdateOrganizationParams {
    organizationId: string;
    payload: UpdateOrganizationPayload;
}

export function useUpdateOrganizationMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            payload,
        }: UpdateOrganizationParams) => {
            const result = await organizationContractClient.updateOrganization({
                params: { organizationId },
                body: payload,
            });

            return OrganizationDtoSchema.parse(result.body);
        },
        onSuccess: (updatedOrganization) => {
            queryClient.setQueryData(
                organizationQueryKeys.get(),
                () => updatedOrganization,
            );

            void queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.get(),
            });
        },
    });
}

export function useCreateLogoUploadUrlMutation(organizationId: string) {
    return useMutation({
        mutationFn: async (filename: string) => {
            const result = await organizationContractClient.createLogoUploadUrl(
                {
                    params: { organizationId },
                    body: { name: filename },
                },
            );
            return result.body;
        },
    });
}

interface UpdateOrganizationProfileParams {
    organizationId: string;
    payload: OrganizationProfile;
}

export function useUpdateOrganizationProfileMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            payload,
        }: UpdateOrganizationProfileParams) => {
            const result =
                await organizationContractClient.updateOrganizationProfile({
                    params: { organizationId },
                    body: payload,
                });

            return OrganizationDtoSchema.parse(result.body);
        },
        onSuccess: (updatedOrganization) => {
            queryClient.setQueryData(
                organizationQueryKeys.get(),
                () => updatedOrganization,
            );

            void queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.get(),
            });
        },
    });
}

export function useRemoveUnonboardedOrganizationsMutation() {
    return useMutation({
        mutationFn: async () => {
            await organizationContractClient.removeUnonboardedOrganizations();
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.get(),
            });
        },
    });
}
